import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, ArrowLeft, Shield, Zap, Ghost, Clock, Lock, Users } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  return (
    <>
      <SEO 
        title="About Us"
        description="Learn about Instant-Email, the free temporary disposable email service that protects your privacy with instant email addresses."
        path="/about"
      />
      
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Mail className="w-8 h-8 text-primary" />
              <span className="font-heading text-xl font-semibold text-foreground">Instant-Email</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-foreground hover:text-primary">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <div className="pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-4xl font-bold text-foreground mb-4">About Instant-Email</h1>
            <p className="text-xl text-muted-foreground mb-12">
              Your privacy-first solution for temporary email addresses
            </p>

            {/* Mission */}
            <Card className="bg-card border-border mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
                <p className="text-muted-foreground leading-relaxed">
                  At Instant-Email, we believe that your privacy online should be effortless. We created a simple, 
                  fast, and secure way to generate temporary email addresses that protect your real identity from 
                  spam, tracking, and unwanted solicitations. Our mission is to give everyone free access to 
                  privacy-protecting tools without compromising on ease of use.
                </p>
              </CardContent>
            </Card>

            {/* What We Do */}
            <Card className="bg-card border-border mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6">What We Do</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Instant-Email provides free, temporary disposable email addresses that you can use anywhere. 
                  Whether you're signing up for a newsletter, testing a service, or protecting your primary 
                  inbox, we've got you covered.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Instant Generation</h3>
                      <p className="text-sm text-muted-foreground">
                        Generate a temporary email address in seconds, no registration required.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Privacy Protection</h3>
                      <p className="text-sm text-muted-foreground">
                        Keep your real email address hidden and protect yourself from spam.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Auto-Delete</h3>
                      <p className="text-sm text-muted-foreground">
                        All emails automatically expire and delete after 24 hours.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Ghost className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Anonymous</h3>
                      <p className="text-sm text-muted-foreground">
                        No personal information required. Stay completely anonymous.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Why Choose Us */}
            <Card className="bg-card border-border mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-4">Why Choose Instant-Email?</h2>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1">100% Free</h3>
                      <p className="text-sm text-muted-foreground">
                        Our service is completely free with no hidden costs or premium tiers.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1">No Registration Required</h3>
                      <p className="text-sm text-muted-foreground">
                        Start using temporary emails immediately without creating an account.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1">Open Source Friendly</h3>
                      <p className="text-sm text-muted-foreground">
                        Built with modern, open-source technologies for transparency.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Users className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1">Optional Accounts</h3>
                      <p className="text-sm text-muted-foreground">
                        Create an account to save and manage multiple temporary addresses.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How It Works */}
            <Card className="bg-card border-border mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6">How It Works</h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Generate an Address</h3>
                      <p className="text-sm text-muted-foreground">
                        Visit our homepage and instantly receive a temporary email address.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Use It Anywhere</h3>
                      <p className="text-sm text-muted-foreground">
                        Copy the address and use it for signups, verifications, or any service.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Receive Emails</h3>
                      <p className="text-sm text-muted-foreground">
                        All incoming emails appear instantly in your inbox on our site.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Auto-Cleanup</h3>
                      <p className="text-sm text-muted-foreground">
                        After 24 hours, the address and all emails are automatically deleted.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact CTA */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-semibold mb-4">Have Questions?</h2>
                <p className="text-muted-foreground mb-6">
                  Check out our FAQ or get in touch with us directly.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/faq">
                    <Button className="rounded-full">
                      View FAQ
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button variant="outline" className="rounded-full">
                      Contact Us
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
